import { Option } from '@sapphire/result';
import { BaseMapper, type Nullable } from '@ps/utils';
import type {
  SupplierBlend,
  SupplierBlendFlexibilityMode,
  SupplierBlendGroup,
} from '@ps/survey-utils';

import { SupplierBlendDto } from '../dtos/supplier-blend.dto';

// Backend flexibility-type keys → domain mode. Any other key falls back to global.
const FLEXIBILITY_MODE_BY_KEY: Record<number, SupplierBlendFlexibilityMode> = {
  1: 'selective',
  2: 'none',
};

export class SupplierBlendMapper extends BaseMapper<
  SupplierBlendDto,
  SupplierBlend
> {
  public fromDto(dto: SupplierBlendDto): SupplierBlend {
    const groups = this._mapGroups(dto);

    return {
      id: dto.id,
      name: dto.config_name,
      buyerId: dto.buyer_id,
      isDefault: dto.is_default,
      isActive: dto.is_active,
      isLocked: dto.is_locked,
      locale: dto.locale,
      flexibilityMode: this._mapFlexibilityMode(dto, groups),
      globalFlexibilityPercentage: dto.blend_flexibility,
      groupNames: dto.suppliers.map((supplier) => supplier.group_name),
      groups,
    };
  }

  private _mapGroups(dto: SupplierBlendDto): SupplierBlendGroup[] {
    return dto.suppliers.map((supplier) => ({
      groupName: supplier.group_name,
      members: supplier.supplier_id.map((member) => ({
        id: member.id,
        name: member.name,
        allocation: this._mapNumericValue(member.allocation),
      })),
      allocationPercentage: this._mapNumericValue(
        supplier.allocation_percentage,
      ),
      flexibilityPercentage: this._mapNumericValue(
        supplier.flexibility_percentage,
      ),
      isCustomAllocation: supplier.is_custom_allocation,
    }));
  }

  // Absence is a real state — null means "no allocation set" — so this returns
  // Nullable<number> rather than escaping an Option; 0 stays a valid value.
  private _mapNumericValue(
    value: Nullable<number | string> | undefined,
  ): Nullable<number> {
    if (typeof value !== 'number' && typeof value !== 'string') {
      return null;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private _mapFlexibilityMode(
    dto: SupplierBlendDto,
    groups: SupplierBlendGroup[],
  ): SupplierBlendFlexibilityMode {
    if (dto.flexibilitySelectedType) {
      return Option.from(
        FLEXIBILITY_MODE_BY_KEY[dto.flexibilitySelectedType.key],
      ).unwrapOr('global');
    }
    return groups.some((group) =>
      Option.from(group.flexibilityPercentage).isSome(),
    )
      ? 'selective'
      : 'global';
  }
}
